import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpOrderService from "@plannel/services/fp/fp-order-service";
import ConfirmDialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ColorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old(OrderTypeMasterPopup) validRules required 2개(orderTpCd/orderStrategyTpCd) —
// Stock.js §REQUIRED_FIELDS 패턴. old의 orderEfficiency validFunc(양수 검증)은 별개 규칙(criteria
// "validFunc")이라 이 표로 흡수하지 않음 — 필요 시 별도 이관.
const REQUIRED_FIELDS = [
  { field: "orderTpCd", label: "FP_ORDER_TP_CD" },
  { field: "orderStrategyTpCd", label: "FP_ORDER_STRATEGY" }
];

// TODO(MIG-T2): [order/OrderTypePopup] isDeletable 기반 삭제 제한(old
//   gridView.setCheckableExpression("value['isDeletable']")) 미이관.
//   old: isDeletable=false인 행은 체크박스 자체가 비활성화되어 삭제 대상에서 원천 배제.
//   사유: new 어떤 골든 레퍼런스도 rowSelection.isRowSelectable 같은 행단위 삭제 제한을 아직
//   쓰지 않아 확립된 패턴이 없음 — 우선 전 행 선택 가능하게 두고, 삭제 시 백엔드 제약(참조 중인
//   주문타입 삭제 실패 등)에 위임한다.

/**
 * 주문 타입(Order Type) 조회/선택 + 관리 팝업 — CustomerPopup.js와 동일 패턴. old는 select
 * 전용(`OrderTypePopup.jsx`)/master 전용(`OrderTypeMasterPopup.jsx`) 두 파일로 나뉘어 있었으나,
 * new는 `editable` prop 하나로 모드를 고정한다(§7).
 *
 * props: open, onClose(), planScope, onSelect(row), editable?(기본 false)
 */
const OrderTypePopup = ({ t, open, onClose, planScope, onSelect, editable = false }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const planStrategyValuesRef = useCommonCodeValues("FP_PLAN_STRATEGY");
  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_ORDER_TYPE_MST"), viewName: "" }), [t]);

  const columnDefs = useMemo(() => ([
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: t("FP_PLAN_SCOPE"), field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old setCodeColumnStyle 대응 — 관리 모드에서도 신규 행(id 없음)만 코드 편집 가능, 저장된 행은 잠금.
    { headerName: t("FP_ORDER_TP_CD"), field: "orderTpCd", colId: "orderTpCd", cellClass: "stringType", width: 160,
      editable: params => editable && !params.data?.id },
    { headerName: t("FP_ORDER_STRATEGY"), field: "orderStrategyTpCd", colId: "orderStrategyTpCd", width: 110, type: ["centerAligned"],
      editable, cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_PLAN_STRATEGY", planStrategyValuesRef, t) },
    { headerName: t("FP_PST"), field: "orderPst", colId: "orderPst", width: 160, type: ["dateTimeColumn", "centerAligned"], editable },
    { headerName: t("FP_TO_STOCK_YN"), field: "toStockYn", colId: "toStockYn", width: 100, type: ["booleanColumn", "centerAligned"], editable },
    { headerName: t("FP_EFFICIENCY"), field: "orderEfficiency", colId: "orderEfficiency", width: 100, type: ["numberColumn", "rightAligned"], editable },
    { headerName: t("FP_CANC_ON_LATE_YN"), field: "cancOnLateYn", colId: "cancOnLateYn", width: 100, type: ["booleanColumn", "centerAligned"], editable },
    {
      headerName: t("FP_COL_AUDIT"), groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: t("updatedTs"), field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: t("updatedBy"), field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
        { headerName: t("createdTs"), field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: t("createdBy"), field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" }
      ]
    }
  ]), [t, editable, planStrategyValuesRef]);

  const loadOrderTypes = () => {
    if (!planScope) return;
    fpOrderService.getOrderTypes({ planScope })
      .then(response => setRowData(isEmpty(response.data) ? [] : response.data))
      .catch(e => { debug(e); setRowData([]); })
      .then(() => DataState.initialize(gridRef.current?.api));
  };

  useEffect(() => {
    if (!open || !planScope) return;
    loadOrderTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope]);

  const confirmSelection = (row) => { if (row) { onSelect?.(row); onClose(); } };
  const onRowDoubleClicked = (e) => { if (!editable) confirmSelection(e.data); };

  const insertRow = () => {
    const newRow = {
      id: null, planScope, orderTpCd: "", orderStrategyTpCd: "FORWARD", orderPst: null,
      toStockYn: false, orderEfficiency: 1, cancOnLateYn: false
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeRows = () => {
    gridRef.current.api.stopEditing();
    const selected = gridRef.current.api.getSelectedRows();
    if (selected.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }
    fpOrderService.removeOrderTypes(selected)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
      .catch(e => { debug(e); setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }); })
      .then(() => loadOrderTypes());
  };

  const saveRows = () => {
    gridRef.current.api.stopEditing();
    const changes = DataState.getAllStateData(gridRef.current.api);
    if (changes.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpOrderService.saveOrderTypes(changes)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { debug(e); setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); })
      .then(() => loadOrderTypes());
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveRows();
    else if (action === "DELETE") removeRows();
  };

  return (
    <>
      <MuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editable ? t("FP_ORDER_TYPE_MST") : t("FP_ORDER_TYPE_SELECT")}
        </DialogTitle>
        <DialogContent>
          {editable && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <AddButton onClick={insertRow} />
              <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
              <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
            </Box>
          )}
          <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              onRowDoubleClicked={onRowDoubleClicked}
              {...gridOptions}
              rowSelection={editable
                ? { ...gridOptions.rowSelection, headerCheckbox: true, checkboxes: true }
                : { mode: "singleRow", checkboxes: false, enableClickSelection: true }}
            />
          </Box>
        </DialogContent>
      </MuiDialog>

      <ConfirmDialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(OrderTypePopup);
